import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Zap, Calendar, TrendingDown, Sparkles, Rocket } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlansSection() {
  const [plans, setPlans] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/job-plans`),
        axios.get(`${API}/companies/me`),
      ]);
      setPlans(p.data);
      setCompanyData(c.data);
    } catch (err) {
      console.error("[PlansSection] load failed", err);
    }
  };
  useEffect(() => { load(); }, []);

  const subscribe = async (plan) => {
    if (!companyData?.has_profile) return toast.error("Cadastre sua empresa em 'Meu perfil' primeiro.");
    if (!window.confirm(`Assinar plano ${plan.name} por R$ ${plan.final_price_brl.toFixed(2)}? O valor será debitado da carteira.`)) return;
    setBusy(plan.plan_id);
    try {
      await axios.post(`${API}/companies/subscribe`, { plan_id: plan.plan_id });
      toast.success(`Plano ${plan.name} ativo!`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(null);
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-gradient-to-br from-primary/10 to-transparent border-primary/40" data-testid="plans-header">
        <div className="flex items-center gap-3">
          <Rocket className="w-6 h-6 text-primary" />
          <div>
            <div className="font-bold">Planos para empresas publicarem vagas</div>
            <div className="text-xs text-muted-foreground">Desconto progressivo — quanto maior o prazo, maior o desconto.</div>
          </div>
        </div>
      </Card>

      {companyData?.active_subscription && (
        <Card className="p-3 bg-green-500/10 border-green-500/40 text-sm" data-testid="plans-active-badge">
          ✓ Plano ativo: <strong>{companyData.active_subscription.plan_name}</strong> · expira em {new Date(companyData.active_subscription.expires_at).toLocaleDateString()}
        </Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {plans.map(p => (
          <Card key={p.plan_id} className={`p-5 relative overflow-hidden ${p.discount_pct >= 40 ? "border-primary/50 bg-primary/5" : ""}`} data-testid={`plan-${p.plan_id}`}>
            {p.discount_pct > 0 && (
              <div className="absolute top-2 right-2">
                <Badge className="bg-orange-500 text-[10px] gap-1">
                  <TrendingDown className="w-3 h-3" /> -{p.discount_pct.toFixed(0)}%
                </Badge>
              </div>
            )}
            {p.discount_pct >= 40 && (
              <div className="absolute top-2 left-2">
                <Badge className="bg-primary text-[10px] gap-1"><Sparkles className="w-3 h-3" /> Melhor</Badge>
              </div>
            )}
            <div className="flex items-center gap-2 mb-2 mt-4">
              <Calendar className="w-4 h-4 text-primary" />
              <div className="font-bold text-lg">{p.name}</div>
            </div>
            <div className="text-3xl font-bold text-primary" data-testid={`plan-price-${p.plan_id}`}>R$ {p.final_price_brl.toFixed(2)}</div>
            {p.discount_pct > 0 && (
              <div className="text-xs text-muted-foreground line-through">de R$ {p.base_price_brl.toFixed(2)}</div>
            )}
            <div className="text-xs text-muted-foreground">R$ {p.per_day_brl.toFixed(2)}/dia · {p.days} dia(s)</div>
            <ul className="text-xs mt-3 space-y-1">
              {(p.features || []).map((f, i) => (
                <li key={i} className="flex items-center gap-1">
                  <Zap className="w-3 h-3 text-primary" /> {f}
                </li>
              ))}
            </ul>
            <Button
              data-testid={`plan-subscribe-${p.plan_id}`}
              size="sm"
              onClick={() => subscribe(p)}
              disabled={busy === p.plan_id}
              className="w-full rounded-full mt-4"
            >
              {busy === p.plan_id ? "Processando..." : "Assinar"}
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
}
